import React from 'react';
import styles from './index.module.scss';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { EditReviewDto } from '@interfaces-clean/reviews.interface';
import { useTranslation } from '@hooks/index';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type Props = {
    studyRef: string;
    review: EditReviewDto;
};

const DataListingsSection = ({ studyRef, review }: Props) => {
    const { t } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit.activeReviewTab.tabs.reviewPlan.dataListings',
    });
    const { t: tCommon } = useTranslation('common');

    const {
        data: config,
        isLoading: configLoading,
        error: configError,
    } = SubjectMonitoringReviewAPI.useGetConfigForReview(studyRef, review.recordId);

    const configDataListingIds = config?.dataListings.map((dl) => dl.dataListingId) ?? [];

    const { data: listingDetails, isLoading: detailsLoading } = SubjectMonitoringAPI.useGetSmDataListings(studyRef, {
        configuredDataTableIds: configDataListingIds,
    });

    const nameByDataListingId = Object.fromEntries((listingDetails ?? []).map((dl) => [dl.id, dl.name]));

    const dataListings = (config?.dataListings ?? []).map((dl) => ({
        id: dl.dataListingId,
        name: nameByDataListingId[dl.dataListingId] ?? dl.dataListingId,
        watchedCount: dl.columns.length,
    }));

    const isLoading = configLoading || (config != null && detailsLoading);
    const isError = configError != null;

    const renderBody = () => {
        if (isLoading) {
            return <Loader />;
        }

        if (isError) {
            return <InfoField type="Error" message={tCommon('errorMessage')} />;
        }

        if (dataListings.length === 0) {
            return (
                <div data-testid="data-listings-empty" className={styles.noDataListings}>
                    {t('noDataListings')}
                </div>
            );
        }

        return (
            <ul data-testid="data-listings-list" className={styles.dataListingsList}>
                {dataListings.map((dataListing) => (
                    <li key={dataListing.id} data-testid="data-listing-row" className={styles.dataListingRow}>
                        <span className={styles.dataListingName}>{dataListing.name}</span>
                        <span className={styles.dataListingWatched}>
                            {dataListing.watchedCount} {t('onWatch')}
                        </span>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <Card
            data-testid="data-listings-card"
            className={styles.gridCell}
            bodyClassName={styles.gridCellInner}
            data-type="data-listings"
            title={t('title')}
        >
            {renderBody()}
        </Card>
    );
};

export default DataListingsSection;
